import { useState } from 'react';
import toast from 'react-hot-toast';
import { Dispatchers, Actions } from '@client/new/helpers/types';
import { getDispatchers } from '@client/new/helpers/types-utils';
import {
  PortalUser,
  PortalUserInput,
  PortalUserRole,
} from '../model/PortalUserList.types';
import { ROLE_LABELS } from './PortalUserList.viewModel.mappers';

const CREATOR_ROLE: PortalUserRole = 3;
const DEFAULT_ROLE: PortalUserRole = 2;

const PARTNER_REQUIRED_MESSAGE =
  'Users with the Creator role require a selected Partner.';
const EMAIL_REQUIRED_MESSAGE = 'E-mail address is required.';
const SAVE_FALLBACK_MESSAGE =
  'An error occured. Please check your data and try again.';

type RoleOption = { value: PortalUserRole; label: string };

const ROLE_OPTIONS: RoleOption[] = ([1, 2, 3, 4] as const).map((value) => ({
  value,
  label: ROLE_LABELS[value],
}));

export const toRoleOptions = (isAdministrator: boolean): RoleOption[] =>
  isAdministrator
    ? ROLE_OPTIONS
    : ROLE_OPTIONS.filter((option) => option.value !== 1);

export const toDraft = (user: PortalUser | null): PortalUserInput =>
  user
    ? { ...user }
    : {
        id: null,
        username: null,
        firstName: '',
        lastName: '',
        email: '',
        role: DEFAULT_ROLE,
        influencerId: null,
        isActive: true,
      };

const deriveUsername = (firstName: string, lastName: string): string =>
  `${firstName}${lastName}`.replace(/\s/g, '').toLowerCase();

type NameField = 'firstName' | 'lastName';

export const changeName = (
  draft: PortalUserInput,
  field: NameField,
  value: string,
  isModerator: boolean,
): PortalUserInput => {
  const next = { ...draft, [field]: value };
  if (isModerator && (next.firstName || next.lastName)) {
    return { ...next, username: deriveUsername(next.firstName, next.lastName) };
  }
  return next;
};

export const changeRole = (
  draft: PortalUserInput,
  role: PortalUserRole,
): PortalUserInput => ({
  ...draft,
  role,
  influencerId: role === CREATOR_ROLE ? draft.influencerId : null,
});

export const validateDraft = (draft: PortalUserInput): string[] => [
  ...(draft.role === CREATOR_ROLE &&
  !(draft.influencerId && draft.influencerId > 0)
    ? [PARTNER_REQUIRED_MESSAGE]
    : []),
  ...(draft.email ? [] : [EMAIL_REQUIRED_MESSAGE]),
];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

export const toSaveErrorMessage = (error: unknown): string => {
  if (!isRecord(error)) return SAVE_FALLBACK_MESSAGE;
  const response = error.response;
  if (
    isRecord(response) &&
    typeof response.data === 'string' &&
    response.data
  ) {
    return response.data;
  }
  if (typeof error.message === 'string' && error.message) return error.message;
  return SAVE_FALLBACK_MESSAGE;
};

type PortalUserFormActionMap = {
  SetFirstName: string;
  SetLastName: string;
  SetEmail: string;
  SetRole: PortalUserRole;
  SetInfluencerId: { influencerId: number | null };
  Submit: null;
  Close: null;
};

type PortalUserFormVM = {
  title: string;
  draft: PortalUserInput;
  roleOptions: RoleOption[];
  showPartner: boolean;
  errors: string[];
  isSaving: boolean;
} & Dispatchers<PortalUserFormActionMap>;

type Params = {
  user: PortalUser | null;
  isAdministrator: boolean;
  isModerator: boolean;
  save: (input: PortalUserInput) => Promise<void>;
  onClose: () => void;
};

export const usePortalUserFormViewModel = ({
  user,
  isAdministrator,
  isModerator,
  save,
  onClose,
}: Params): PortalUserFormVM => {
  const [draft, setDraft] = useState(() => toDraft(user));
  const [errors, setErrors] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const submit = async () => {
    const validationErrors = validateDraft(draft);
    setErrors(validationErrors);
    if (validationErrors.length > 0) return;

    setIsSaving(true);
    try {
      await save(draft);
      toast.success('User saved');
      onClose();
    } catch (error) {
      setErrors([toSaveErrorMessage(error)]);
    } finally {
      setIsSaving(false);
    }
  };

  const _dispatch = (action: Actions<PortalUserFormActionMap>) => {
    switch (action.type) {
      case 'SetFirstName':
        setDraft((prev) =>
          changeName(prev, 'firstName', action.payload, isModerator),
        );
        return;
      case 'SetLastName':
        setDraft((prev) =>
          changeName(prev, 'lastName', action.payload, isModerator),
        );
        return;
      case 'SetEmail':
        setDraft((prev) => ({ ...prev, email: action.payload }));
        return;
      case 'SetRole':
        setDraft((prev) => changeRole(prev, action.payload));
        return;
      case 'SetInfluencerId':
        setDraft((prev) => ({
          ...prev,
          influencerId: action.payload.influencerId,
        }));
        return;
      case 'Submit':
        void submit();
        return;
      case 'Close':
        if (!isSaving) onClose();
        return;
    }
  };

  return {
    title: user?.firstName ? `${user.firstName} ${user.lastName}` : 'New User',
    draft,
    roleOptions: toRoleOptions(isAdministrator),
    showPartner: draft.role === CREATOR_ROLE,
    errors,
    isSaving,
    ...getDispatchers<PortalUserFormActionMap>(_dispatch),
  };
};
